import React from 'react'
import i18n from 'config/i18n'
import { PLATFORM_ENUM } from 'services/api/constants'
import { GridRenderCellParams } from '@mui/x-data-grid-pro'

import { CampaignState } from 'state/campaigns'
import { TaskState } from 'state/tasks'
import { RegistrationResultState } from 'state/registrations'
import { PartiposterProfile, SocialMediaAccount } from 'state/submissions'
import { DetailModalCell } from 'components/Shared/MuiTable/Cell/DetailModalCell'
import { RegistrationBlock } from 'services/api/registrations'
import { WaitListTableRows, GridColDefProps } from './constants'
import { showDefaultIgPlatform } from 'components/utils/campaign'
import { ProfileCell } from 'components/Shared/MuiTable/Cell/ProfileCell'
import { SocialMediaInfoCell } from 'components/Shared/MuiTable/Cell/SocialMediaInfoCell'
import { FIELDS_TYPE } from 'components/utils/filterAndSort'

export const getColumns = (
    campaign: CampaignState,
    searchablePlatforms: PLATFORM_ENUM[],
    followerRequirementsPlatform: PLATFORM_ENUM[],
    updateViewedRow?: (id: string) => void,
): GridColDefProps[] => {
    if (!campaign) return []
    const hasRequirements = campaign.requirements.length > 0
    const hasPlatform = (platform: PLATFORM_ENUM) => {
        return searchablePlatforms.includes(platform) || followerRequirementsPlatform.includes(platform)
    }

    const cols: GridColDefProps[] = [
        {
            field: 'profile',
            type: 'string',
            headerName: i18n.t('submission.table.profile'),
            width: 160,
            minWidth: 60,
            sortable: false,
            renderCell: (props: GridRenderCellParams) => <ProfileCell {...props} />,
            subFields: [
                {
                    field: 'name',
                    headerName: i18n.t('submission.table.nameLabel'),
                },
                {
                    field: 'gender',
                    headerName: i18n.t('submission.table.genderLabel'),
                },
                {
                    field: 'age',
                    headerName: i18n.t('submission.table.ageLabel'),
                },
                ...(hasRequirements
                    ? [
                          {
                              field: 'meetRequirement',
                              headerName: i18n.t('submission.table.meetRequirementsLabel'),
                          },
                      ]
                    : []),
            ],
        },
        {
            field: 'socialMediaInfo',
            type: 'string',
            headerName: i18n.t('submission.table.socialMediaInfo'),
            width: 180,
            minWidth: 60,
            sortable: false,
            renderCell: (props: GridRenderCellParams) => {
                const handleUpdateViewed = () => {
                    if (updateViewedRow) {
                        updateViewedRow(props.row.uuid)
                    }
                }
                return <SocialMediaInfoCell {...props} onClick={handleUpdateViewed} />
            },
            subFields: [
                ...(showDefaultIgPlatform(searchablePlatforms, followerRequirementsPlatform)
                    ? [
                          {
                              field: FIELDS_TYPE.IG_FOLLOWERS,
                              headerName: i18n.t('submission.table.instagramFollowersLabel'),
                          },
                          {
                              field: FIELDS_TYPE.IG_USERNAME,
                              headerName: i18n.t('submission.table.instagramUsernameLabel'),
                          },
                      ]
                    : []),
                ...(hasPlatform(PLATFORM_ENUM.FB)
                    ? [
                          {
                              field: FIELDS_TYPE.FB_FRIENDS,
                              headerName: i18n.t('submission.table.facebookFriendsLabel'),
                          },
                          {
                              field: FIELDS_TYPE.FB_USERNAME,
                              headerName: i18n.t('submission.table.facebookUsernameLabel'),
                          },
                      ]
                    : []),
                ...(hasPlatform(PLATFORM_ENUM.TIKTOK)
                    ? [
                          {
                              field: FIELDS_TYPE.TIKTOK_FOLLOWERS,
                              headerName: i18n.t('submission.table.tikTokFollowersLabel'),
                          },
                          {
                              field: FIELDS_TYPE.TIKTOK_USERNAME,
                              headerName: i18n.t('submission.table.tikTokUsernameLabel'),
                          },
                      ]
                    : []),
                ...(hasPlatform(PLATFORM_ENUM.TWITTER)
                    ? [
                          {
                              field: FIELDS_TYPE.TWITTER_FOLLOWERS,
                              headerName: i18n.t('submission.table.twitterFollowersLabel'),
                          },
                          {
                              field: FIELDS_TYPE.TWITTER_USERNAME,
                              headerName: i18n.t('submission.table.twitterUsernameLabel'),
                          },
                      ]
                    : []),
                ...(hasPlatform(PLATFORM_ENUM.XIAOHONGSHU)
                    ? [
                          {
                              field: FIELDS_TYPE.XIAOHONGSHU_FOLLOWERS,
                              headerName: i18n.t('submission.table.XHSFollowersLabel'),
                          },
                          {
                              field: FIELDS_TYPE.XIAOHONGSHU_USERNAME,
                              headerName: i18n.t('submission.table.XHSUsernameLabel'),
                          },
                      ]
                    : []),
            ],
        },
        {
            field: 'action',
            type: 'string',
            headerName: i18n.t('submission.table.actionsLabel'),
            width: 114,
            renderCell: (props: GridRenderCellParams) => <DetailModalCell {...props} />,
            sortable: false,
            filterable: false,
        },
    ]
    return cols
}

export const getRows = (
    campaign: CampaignState,
    tasks: TaskState[],
    registrations: RegistrationResultState[],
    profileById: Record<string, PartiposterProfile>,
    registrationFormBlocksById: Record<string, RegistrationBlock>,
    onOpenActionDetailModal: any,
): WaitListTableRows[] => {
    return registrations.map(registration => {
        const profile = profileById[registration.partiposterId]
        const { social_media_accounts: socialMediaAccounts, name } = profile
        const igProfile = socialMediaAccounts.filter(
            (account: SocialMediaAccount) => account.platform === PLATFORM_ENUM.IG,
        )[0]
        const fbProfile = socialMediaAccounts.filter(
            (account: SocialMediaAccount) => account.platform === PLATFORM_ENUM.FB,
        )[0]
        const tiktokProfile = socialMediaAccounts.filter(
            (account: SocialMediaAccount) => account.platform === PLATFORM_ENUM.TIKTOK,
        )[0]
        const twitterProfile = socialMediaAccounts.filter(
            (account: SocialMediaAccount) => account.platform === PLATFORM_ENUM.TWITTER,
        )[0]
        const xiaohongshuProfile = socialMediaAccounts.filter(
            (account: SocialMediaAccount) => account.platform === PLATFORM_ENUM.XIAOHONGSHU,
        )[0]
        const hasFbTask = tasks.find(task => ['facebook_post'].includes(task.type))
        const { unmet_requirement_types } = registration
        const hasRequirements = campaign.requirements.length > 0

        return {
            id: registration.uuid,
            uuid: registration.uuid,
            name: {
                value: name,
            },
            profile: {
                unmetRequirement: unmet_requirement_types,
                profile,
                hasRequirements,
            },
            socialMediaInfo: {
                profiles: [igProfile, fbProfile, tiktokProfile, twitterProfile, xiaohongshuProfile],
            },
            action: {
                registration: registration,
                registrationFormBlocksById: registrationFormBlocksById,
                campaignUuid: campaign.uuid,
                profile: profile,
                igUserName: igProfile && igProfile.username ? igProfile.username : undefined,
                hasFbTask: hasFbTask,
                fbUserName: fbProfile && fbProfile.username ? fbProfile.username : undefined,
                onOpenActionDetailModal: onOpenActionDetailModal,
            },
        }
    })
}
